import {
  ECategoryType,
  type EProductScoringDays,
  type EWarehouseProductTrendScenario,
  type IConversionFunnelStage,
  type IOssReport,
  type IReportCategoryLanguage,
  type IReportPhotoDuplicate,
  type IReportProductLanguage,
  type IReportProductMetaDescription,
  type IReportProductMetaKeywords,
  type IReportProductScoring,
  type IReportPropertyLanguage,
  type IReportPropertyValueLanguage,
  type IReportWarehouseProductPhotoComparison,
  type IReportWarehouseProductsPairing,
  type IReportWarehouseProductsTrends,
  type IReportWarehouseProductToImport,
  type IWarehouseProductSuggestions,
} from '@mahawi/eshop-common/src/types';
import { createSlice } from '@reduxjs/toolkit';

import { type IIsoDateRange } from '../../fragments/date-range';
import {
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
} from '../async-state';
import {
  type IReportState,
  type IReportWarehouseCategoryTrends,
  type IWarehouseProductChildCategoryEstimate,
} from './types';

export const preloadedState: IReportState = {
  inProcess: false,
  photoDuplicates: undefined,
  categoryLanguages: [],
  productLanguages: [],
  productMetaDescription: [],
  productMetaKeywords: [],
  productScoring: [],
  propertyLanguages: [],
  propertyValueLanguages: [],
  warehouseProductPhotoComparison: undefined,
  warehouseProductToImport: undefined,
  warehouseProductChildCategoryEstimates: undefined,
  warehouseProductChildCategorySearchUnavailable: false,
  warehouseProductCategorySuggestions: undefined,
  warehouseProductTrend: undefined,
  warehouseCategoryTrend: undefined,
  warehouseProductsPairing: undefined,
  conversionFunnel: undefined,
  updatedAt: undefined,
};

const reportSlice = createSlice({
  name: 'Report',
  initialState: preloadedState,
  reducers: {
    reportProductLanguagesLoad: {
      reducer(state) {
        state.inProcess = true;
        state.productLanguages = undefined;
      },
      prepare(onlyActiveLanguages: boolean, limit: number) {
        return { payload: { onlyActiveLanguages, limit } };
      },
    },
    reportProductLanguagesLoadResponse: {
      reducer(
        state,
        action: { payload: { report: IReportProductLanguage[] } },
      ) {
        state.inProcess = false;
        state.productLanguages = action.payload.report;
        state.updatedAt = Date.now();
      },
      prepare(report: IReportProductLanguage[]) {
        return { payload: { report } };
      },
    },
    reportCategoryLanguagesLoad: {
      reducer(state) {
        state.inProcess = true;
        state.categoryLanguages = undefined;
      },
      prepare(onlyActiveLanguages: boolean, limit: number) {
        return { payload: { onlyActiveLanguages, limit } };
      },
    },
    reportCategoryLanguagesLoadResponse: {
      reducer(
        state,
        action: { payload: { report: IReportCategoryLanguage[] } },
      ) {
        state.inProcess = false;
        state.categoryLanguages = action.payload.report;
        state.updatedAt = Date.now();
      },
      prepare(report: IReportCategoryLanguage[]) {
        return { payload: { report } };
      },
    },
    reportProductMetaDescriptionLoad: {
      reducer(state) {
        state.inProcess = true;
        state.productMetaDescription = undefined;
      },
      prepare(onlyActiveLanguages: boolean) {
        return { payload: { onlyActiveLanguages } };
      },
    },
    reportProductMetaDescriptionLoadResponse: {
      reducer(
        state,
        action: { payload: { report: IReportProductMetaDescription[] } },
      ) {
        state.inProcess = false;
        state.productMetaDescription = action.payload.report;
        state.updatedAt = Date.now();
      },
      prepare(report: IReportProductMetaDescription[]) {
        return { payload: { report } };
      },
    },
    reportProductMetaKeywordsLoad: {
      reducer(state) {
        state.inProcess = true;
        state.productMetaKeywords = undefined;
      },
      prepare(onlyActiveLanguages: boolean) {
        return { payload: { onlyActiveLanguages } };
      },
    },
    reportProductMetaKeywordsLoadResponse: {
      reducer(
        state,
        action: { payload: { report: IReportProductMetaKeywords[] } },
      ) {
        state.inProcess = false;
        state.productMetaKeywords = action.payload.report;
        state.updatedAt = Date.now();
      },
      prepare(report: IReportProductMetaKeywords[]) {
        return { payload: { report } };
      },
    },
    reportProductScoringLoad: {
      reducer(state) {
        state.inProcess = true;
        state.productScoring = undefined;
      },
      prepare(daysInterval: EProductScoringDays, countryTypeIsoCode: string) {
        return { payload: { daysInterval, countryTypeIsoCode } };
      },
    },
    reportProductScoringLoadResponse: {
      reducer(state, action: { payload: { report: IReportProductScoring[] } }) {
        state.inProcess = false;
        state.productScoring = action.payload.report;
        state.updatedAt = Date.now();
      },
      prepare(report: IReportProductScoring[]) {
        return { payload: { report } };
      },
    },
    reportPropertyLanguagesLoad(state) {
      state.inProcess = true;
      state.propertyLanguages = undefined;
    },
    reportPropertyLanguagesLoadResponse: {
      reducer(
        state,
        action: { payload: { report: IReportPropertyLanguage[] } },
      ) {
        state.inProcess = false;
        state.propertyLanguages = action.payload.report;
        state.updatedAt = Date.now();
      },
      prepare(report: IReportPropertyLanguage[]) {
        return { payload: { report } };
      },
    },
    reportPropertyValueLanguagesLoad(state) {
      state.inProcess = true;
      state.propertyValueLanguages = undefined;
    },
    reportPropertyValueLanguagesLoadResponse: {
      reducer(
        state,
        action: { payload: { report: IReportPropertyValueLanguage[] } },
      ) {
        state.inProcess = false;
        state.propertyValueLanguages = action.payload.report;
        state.updatedAt = Date.now();
      },
      prepare(report: IReportPropertyValueLanguage[]) {
        return { payload: { report } };
      },
    },
    reportWarehouseProductTrendLoad: {
      reducer(state) {
        state.inProcess = true;
        state.warehouseProductTrend = undefined;
      },
      prepare(
        warehouseUuid: string,
        dayInterval: number,
        warehouseProductsCount: number,
        excludeAssignedProducts: boolean,
        minimalWeight: number,
        maximalWeight: number,
        minimalPrice: number,
        maximalPrice: number,
        scenario: EWarehouseProductTrendScenario,
      ) {
        return {
          payload: {
            warehouse: { uuid: warehouseUuid },
            dayInterval,
            warehouseProductsCount,
            excludeAssignedProducts,
            weight: { minimal: minimalWeight, maximal: maximalWeight },
            price: { minimal: minimalPrice, maximal: maximalPrice },
            scenario,
          },
        };
      },
    },
    reportWarehouseProductTrendLoadResponse: {
      reducer(
        state,
        action: { payload: { report: IReportWarehouseProductsTrends } },
      ) {
        state.inProcess = false;
        state.warehouseProductTrend = action.payload.report;
        state.updatedAt = Date.now();
      },
      prepare(report: IReportWarehouseProductsTrends) {
        return { payload: { report } };
      },
    },
    reportWarehouseCategoryTrendLoad: {
      reducer(state) {
        state.inProcess = true;
        state.warehouseCategoryTrend = undefined;
      },
      prepare(
        warehouseUuid: string,
        dayInterval: number,
        categoriesCount: number,
        excludeAssignedProducts: boolean,
        minimalWeight: number,
        maximalWeight: number,
        minimalPrice: number,
        maximalPrice: number,
        scenario: EWarehouseProductTrendScenario,
      ) {
        return {
          payload: {
            warehouse: { uuid: warehouseUuid },
            dayInterval,
            categoriesCount,
            excludeAssignedProducts,
            weight: { minimal: minimalWeight, maximal: maximalWeight },
            price: { minimal: minimalPrice, maximal: maximalPrice },
            scenario,
          },
        };
      },
    },
    reportWarehouseCategoryTrendLoadResponse: {
      reducer(
        state,
        action: { payload: { report: IReportWarehouseCategoryTrends } },
      ) {
        state.inProcess = false;
        state.warehouseCategoryTrend = action.payload.report;
        state.updatedAt = Date.now();
      },
      prepare(report: IReportWarehouseCategoryTrends) {
        return { payload: { report } };
      },
    },
    reportWarehouseProductToImportLoad: {
      reducer(state) {
        state.inProcess = true;
        state.warehouseProductToImport = undefined;
      },
      prepare(
        warehouseUuid: string,
        minimalWeight: number,
        maximalWeight: number,
        minimalPrice: number,
        maximalPrice: number,
        minimalDescriptionLength: number,
        categoryType: ECategoryType = ECategoryType.CHILD,
        excludeStaleProducts = true,
        includeUnmappedCategories = false,
      ) {
        return {
          payload: {
            warehouse: { uuid: warehouseUuid },
            weight: { minimal: minimalWeight, maximal: maximalWeight },
            price: { minimal: minimalPrice, maximal: maximalPrice },
            description: { minimalLength: minimalDescriptionLength },
            categoryType,
            excludeStaleProducts,
            includeUnmappedCategories,
          },
        };
      },
    },
    reportWarehouseProductToImportLoadResponse: {
      reducer(
        state,
        action: { payload: { report: IReportWarehouseProductToImport[] } },
      ) {
        state.inProcess = false;
        state.warehouseProductToImport = action.payload.report;
        state.updatedAt = Date.now();
      },
      prepare(report: IReportWarehouseProductToImport[]) {
        return { payload: { report } };
      },
    },
    reportPhotoDuplicatesLoad(state) {
      state.inProcess = true;
      state.photoDuplicates = undefined;
    },
    reportPhotoDuplicatesLoadResponse: {
      reducer(state, action: { payload: { report: IReportPhotoDuplicate[] } }) {
        state.inProcess = false;
        state.photoDuplicates = action.payload.report;
        state.updatedAt = Date.now();
      },
      prepare(report: IReportPhotoDuplicate[]) {
        return { payload: { report } };
      },
    },
    reportWarehouseProductPhotoComparisonLoad: {
      reducer(state) {
        state.inProcess = true;
        state.warehouseProductPhotoComparison = undefined;
      },
      prepare(isActiveProducts: boolean) {
        return { payload: { isActiveProducts } };
      },
    },
    reportWarehouseProductPhotoComparisonLoadResponse: {
      reducer(
        state,
        action: {
          payload: { report: IReportWarehouseProductPhotoComparison[] };
        },
      ) {
        state.inProcess = false;
        state.warehouseProductPhotoComparison = action.payload.report;
        state.updatedAt = Date.now();
      },
      prepare(report: IReportWarehouseProductPhotoComparison[]) {
        return { payload: { report } };
      },
    },
    reportWarehouseProductsPairingLoad: {
      reducer(state) {
        state.inProcess = true;
        state.warehouseProductsPairing = undefined;
      },
      prepare(
        warehouseUuid: string,
        excludeStaleProducts = true,
        includeSemanticMatches = false,
      ) {
        return {
          payload: {
            warehouse: { uuid: warehouseUuid },
            excludeStaleProducts,
            includeSemanticMatches,
          },
        };
      },
    },
    reportWarehouseProductsPairingLoadResponse: {
      reducer(
        state,
        action: { payload: { report: IReportWarehouseProductsPairing[] } },
      ) {
        state.inProcess = false;
        state.warehouseProductsPairing = action.payload.report;
        state.updatedAt = Date.now();
      },
      prepare(report: IReportWarehouseProductsPairing[]) {
        return { payload: { report } };
      },
    },
    reportWarehouseProductChildCategoryEstimateLoad: {
      reducer(state) {
        state.inProcess = true;
        state.warehouseProductChildCategoryEstimates = undefined;
        state.warehouseProductChildCategorySearchUnavailable = false;
      },
      prepare(warehouseProductUUIDs: string[], warehouseUUID: string) {
        return {
          payload: {
            warehouseProductUUIDs,
            warehouse: { uuid: warehouseUUID },
          },
        };
      },
    },
    reportWarehouseProductChildCategoryEstimateLoadResponse: {
      reducer(
        state,
        action: {
          payload: {
            estimates: IWarehouseProductChildCategoryEstimate[];
            searchUnavailable: boolean;
          };
        },
      ) {
        state.inProcess = false;
        state.warehouseProductChildCategoryEstimates = action.payload.estimates;
        state.warehouseProductChildCategorySearchUnavailable =
          action.payload.searchUnavailable;
        state.updatedAt = Date.now();
      },
      prepare(
        estimates: IWarehouseProductChildCategoryEstimate[],
        searchUnavailable: boolean = false,
      ) {
        return { payload: { estimates, searchUnavailable } };
      },
    },
    reportWarehouseProductCategorySuggestionLoad: {
      reducer(state) {
        state.warehouseProductCategorySuggestions = undefined;
      },
      prepare(warehouseProductUUIDs: string[]) {
        return { payload: { warehouseProductUUIDs } };
      },
    },
    reportWarehouseProductCategorySuggestionLoadResponse: {
      reducer(
        state,
        action: { payload: { suggestions: IWarehouseProductSuggestions[] } },
      ) {
        state.warehouseProductCategorySuggestions = action.payload.suggestions;
      },
      prepare(suggestions: IWarehouseProductSuggestions[]) {
        return { payload: { suggestions } };
      },
    },
    reportConversionFunnelLoad: {
      reducer(state) {
        state.inProcess = true;
        state.conversionFunnel = undefined;
      },
      prepare(from: string, to: string) {
        return { payload: { from, to } };
      },
    },
    reportConversionFunnelLoadResponse: {
      reducer(
        state,
        action: { payload: { report: IConversionFunnelStage[] } },
      ) {
        state.inProcess = false;
        state.conversionFunnel = action.payload.report;
        state.updatedAt = Date.now();
      },
      prepare(report: IConversionFunnelStage[]) {
        return { payload: { report } };
      },
    },
    reportOssLoad: {
      reducer(state) {
        state.inProcess = true;
        state.oss = undefined;
        state.ossPrevious = undefined;
      },
      prepare(period: IIsoDateRange, previousPeriod: IIsoDateRange) {
        return { payload: { period, previousPeriod } };
      },
    },
    reportOssLoadResponse: {
      reducer(
        state,
        action: { payload: { report: IOssReport; previousReport: IOssReport } },
      ) {
        state.inProcess = false;
        state.oss = action.payload.report;
        state.ossPrevious = action.payload.previousReport;
        state.updatedAt = Date.now();
      },
      prepare(report: IOssReport, previousReport: IOssReport) {
        return { payload: { report, previousReport } };
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      requestFailedMatcher('Report'),
      resetLoadingOnRequestFailed,
    );
  },
});

export const {
  reportCategoryLanguagesLoad,
  reportCategoryLanguagesLoadResponse,
  reportPhotoDuplicatesLoad,
  reportPhotoDuplicatesLoadResponse,
  reportProductLanguagesLoad,
  reportProductLanguagesLoadResponse,
  reportProductMetaDescriptionLoad,
  reportProductMetaDescriptionLoadResponse,
  reportProductMetaKeywordsLoad,
  reportProductMetaKeywordsLoadResponse,
  reportProductScoringLoad,
  reportProductScoringLoadResponse,
  reportPropertyLanguagesLoad,
  reportPropertyLanguagesLoadResponse,
  reportPropertyValueLanguagesLoad,
  reportPropertyValueLanguagesLoadResponse,
  reportWarehouseCategoryTrendLoad,
  reportWarehouseCategoryTrendLoadResponse,
  reportWarehouseProductPhotoComparisonLoad,
  reportWarehouseProductPhotoComparisonLoadResponse,
  reportWarehouseProductTrendLoad,
  reportWarehouseProductTrendLoadResponse,
  reportWarehouseProductToImportLoad,
  reportWarehouseProductToImportLoadResponse,
  reportWarehouseProductsPairingLoad,
  reportWarehouseProductsPairingLoadResponse,
  reportWarehouseProductChildCategoryEstimateLoad,
  reportWarehouseProductChildCategoryEstimateLoadResponse,
  reportWarehouseProductCategorySuggestionLoad,
  reportWarehouseProductCategorySuggestionLoadResponse,
  reportConversionFunnelLoad,
  reportConversionFunnelLoadResponse,
  reportOssLoad,
  reportOssLoadResponse,
} = reportSlice.actions;
export default reportSlice.reducer;
